import React, { useState, useEffect } from 'react';
import SessoesService, { SessaoAtiva } from '../services/sessoesService';
import LogsUsuariosService, { LogUsuario } from '../services/logsUsuariosService';
import AutoTestRunner from '../components/test/AutoTestRunner';

interface Metrica {
  timestamp: number;
  cpu: number;
  memoria: number;
  rede: number;
}

interface StatusServidor {
  status: 'online' | 'offline' | 'degradado';
  uptime: string;
  ultima_verificacao: string;
}

const MonitoramentoSistema: React.FC = () => {
  const [metricas, setMetricas] = useState<Metrica[]>([]);
  const [statusDB, setStatusDB] = useState<'online' | 'offline' | 'degradado'>('online');
  const [statusAPI, setStatusAPI] = useState<'online' | 'offline' | 'degradado'>('online');
  const [cpuAtual, setCpuAtual] = useState(45);
  const [memoriaAtual, setMemoriaAtual] = useState(62);
  const [redeAtual, setRedeAtual] = useState(28);
  const [uptime, setUptime] = useState('0d 0h 0m 0s');
  const [logs, setLogs] = useState<Array<{ timestamp: string; nivel: string; mensagem: string }>>([]);
  const [usuariosOnline, setUsuariosOnline] = useState<SessaoAtiva[]>([]);
  const [logsUsuarios, setLogsUsuarios] = useState<LogUsuario[]>([]);
  const [abaAtiva, setAbaAtiva] = useState<'monitoramento' | 'testes'>('monitoramento');

  // Simular métricas em tempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setCpuAtual(prev => Math.max(0, Math.min(100, prev + (Math.random() - 0.5) * 10)));
      setMemoriaAtual(prev => Math.max(0, Math.min(100, prev + (Math.random() - 0.5) * 5)));
      setRedeAtual(prev => Math.max(0, Math.min(100, prev + (Math.random() - 0.5) * 20)));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Verificar conexão com banco de dados via API intermediária
  useEffect(() => {
    const verificarDB = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/health/database`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('@sistema-up:token')}`,
            'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
          }
        });
        setStatusDB(response.ok ? 'online' : 'offline');
      } catch (error) {
        setStatusDB('offline');
      }
    };

    verificarDB();
    const interval = setInterval(verificarDB, 30000);
    return () => clearInterval(interval);
  }, []);

  // Simular uptime
  useEffect(() => {
    let segundos = 0;
    const interval = setInterval(() => {
      segundos++;
      const dias = Math.floor(segundos / 86400);
      const horas = Math.floor((segundos % 86400) / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);
      const seg = segundos % 60;
      setUptime(`${dias}d ${horas}h ${minutos}m ${seg}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Buscar usuários online
  useEffect(() => {
    const buscarUsuariosOnline = async () => {
      try {
        const usuarios = await SessoesService.buscarUsuariosOnline();
        setUsuariosOnline(usuarios);
      } catch (error) {
        console.error('Erro ao buscar usuários online:', error);
      }
    };

    buscarUsuariosOnline();
    const interval = setInterval(buscarUsuariosOnline, 10000); // Atualizar a cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  // Buscar logs de usuários
  useEffect(() => {
    const buscarLogsUsuarios = async () => {
      try {
        const logs = await LogsUsuariosService.buscarLogsRecentes(20);
        setLogsUsuarios(logs);
      } catch (error) {
        console.error('Erro ao buscar logs de usuários:', error);
      }
    };

    buscarLogsUsuarios();
    const interval = setInterval(buscarLogsUsuarios, 15000); // Atualizar a cada 15 segundos

    return () => clearInterval(interval);
  }, []);

  // Adicionar logs simulados
  useEffect(() => {
    const mensagens = [
      { nivel: 'INFO', mensagem: 'Sistema iniciado com sucesso' },
      { nivel: 'INFO', mensagem: 'Conexão com banco de dados estabelecida' },
      { nivel: 'INFO', mensagem: 'Servidor web rodando na porta 3000' },
      { nivel: 'WARNING', mensagem: 'Uso de memória acima de 60%' },
      { nivel: 'INFO', mensagem: 'Backup automático concluído' },
      { nivel: 'INFO', mensagem: 'Cache limpo automaticamente' },
      { nivel: 'ERROR', mensagem: 'Timeout na requisição externa' },
      { nivel: 'INFO', mensagem: 'Usuário autenticado com sucesso' },
    ];

    const interval = setInterval(() => {
      const log = mensagens[Math.floor(Math.random() * mensagens.length)];
      setLogs(prev => [
        {
          timestamp: new Date().toLocaleTimeString('pt-BR'),
          nivel: log.nivel,
          mensagem: log.mensagem
        },
        ...prev.slice(0, 19)
      ]);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-600 bg-green-100';
      case 'offline': return 'text-red-600 bg-red-100';
      case 'degradado': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getGaugeColor = (value: number) => {
    if (value < 50) return '#22c55e';
    if (value < 75) return '#eab308';
    return '#ef4444';
  };

  const renderGauge = (value: number, label: string, unit: string) => {
    const color = getGaugeColor(value);
    const rotation = (value / 100) * 180 - 90;

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{label}</h3>
        <div className="relative w-48 h-24 mx-auto overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gray-200 rounded-t-full"></div>
          <div
            className="absolute top-0 left-0 w-full h-full rounded-t-full transition-all duration-500"
            style={{
              background: `conic-gradient(${color} ${value * 3.6}deg, transparent ${value * 3.6}deg)`,
              transform: `rotate(${rotation}deg)`,
              transformOrigin: 'bottom center'
            }}
          ></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-16 bg-white rounded-t-full"></div>
        </div>
        <div className="text-center mt-4">
          <span className="text-4xl font-bold" style={{ color }}>{value.toFixed(1)}</span>
          <span className="text-xl text-gray-600 ml-1">{unit}</span>
        </div>
      </div>
    );
  };

  const renderMiniChart = (data: number[], color: string) => {
    const max = Math.max(...data, 100);
    const min = Math.min(...data, 0);
    const range = max - min || 1;

    return (
      <svg className="w-full h-16" viewBox="0 0 100 40" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={data.map((val, i) => {
            const x = (i / (data.length - 1)) * 100;
            const y = 40 - ((val - min) / range) * 35;
            return `${x},${y}`;
          }).join(' ')}
        />
      </svg>
    );
  };

  const cpuHistory = Array.from({ length: 20 }, () => Math.random() * 100);
  const memoriaHistory = Array.from({ length: 20 }, () => 40 + Math.random() * 40);
  const redeHistory = Array.from({ length: 20 }, () => Math.random() * 80);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Monitoramento de Sistema</h1>
        <p className="text-gray-600">Visão geral em tempo real da performance e status do sistema</p>
      </div>

      {/* Abas de Monitoramento e Testes */}
      <div className="flex space-x-1 bg-white rounded-lg shadow p-1 mb-8">
        <button
          onClick={() => setAbaAtiva('monitoramento')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'monitoramento'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📊 Monitoramento
        </button>
        <button
          onClick={() => setAbaAtiva('testes')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'testes'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          ⚡ Test Runner
        </button>
      </div>

      {/* Conteúdo da Aba de Monitoramento */}
      {abaAtiva === 'monitoramento' && (
        <>
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Banco de Dados</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(statusDB)}`}>
                  {statusDB.toUpperCase()}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Última verificação: {new Date().toLocaleTimeString('pt-BR')}</p>
                <p className="mt-1">Latência: 12ms</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">API</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(statusAPI)}`}>
                  {statusAPI.toUpperCase()}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Requisições/min: 245</p>
                <p className="mt-1">Tempo médio: 85ms</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Uptime</h3>
                <span className="px-3 py-1 rounded-full text-sm font-medium text-green-600 bg-green-100">
                  99.9%
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p className="text-2xl font-bold text-gray-900">{uptime}</p>
                <p className="mt-1">Tempo de atividade</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Usuários Online</h3>
                <span className="px-3 py-1 rounded-full text-sm font-medium text-blue-600 bg-blue-100">
                  AO VIVO
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p className="text-2xl font-bold text-gray-900">{usuariosOnline.length}</p>
                <p className="mt-1">Sessões ativas</p>
              </div>
              <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                {usuariosOnline.map((usuario) => (
                  <div key={usuario.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium text-gray-900">{usuario.usuario_nome}</p>
                      <p className="text-gray-500">{usuario.usuario_login}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600">
                        {new Date(usuario.ultima_atividade).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {usuario.filial_atual || 'Sem filial'}
                      </p>
                    </div>
                  </div>
                ))}
                {usuariosOnline.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-xs">
                    Nenhum usuário online
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderGauge(cpuAtual, 'Uso de CPU', '%')}
            {renderGauge(memoriaAtual, 'Uso de Memória', '%')}
            {renderGauge(redeAtual, 'Tráfego de Rede', 'Mbps')}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Histórico de CPU</h3>
              {renderMiniChart(cpuHistory, '#3b82f6')}
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>-20min</span>
                <span>Agora</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Histórico de Memória</h3>
              {renderMiniChart(memoriaHistory, '#8b5cf6')}
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>-20min</span>
                <span>Agora</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Histórico de Rede</h3>
              {renderMiniChart(redeHistory, '#10b981')}
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>-20min</span>
                <span>Agora</span>
              </div>
            </div>
          </div>

          {/* Logs */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Logs do Sistema</h3>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                Limpar Logs
              </button>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
              {logs.map((log, index) => (
                <div key={index} className="mb-2">
                  <span className="text-gray-400">[{log.timestamp}]</span>
                  <span className={`ml-2 ${
                    log.nivel === 'ERROR' ? 'text-red-400' :
                    log.nivel === 'WARNING' ? 'text-yellow-400' :
                    log.nivel === 'SUCCESS' ? 'text-green-400' :
                    'text-blue-400'
                  }`}>
                    [{log.nivel}]
                  </span>
                  <span className="ml-2 text-gray-300">{log.mensagem}</span>
                </div>
              ))}
              {logs.length === 0 && (
                <div className="text-gray-500 text-center py-8">
                  Nenhum log disponível
                </div>
              )}
            </div>
          </div>

          {/* Log de Usuários */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Log de Usuários</h3>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                Limpar Logs
              </button>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
              {logsUsuarios.map((log, index) => (
                <div key={index} className="mb-2">
                  <span className="text-gray-400">
                    [{new Date(log.data_acao).toLocaleString('pt-BR')}]
                  </span>
                  <span className={`ml-2 ${
                    log.acao === 'LOGIN' ? 'text-green-400' :
                    log.acao === 'LOGOUT' ? 'text-red-400' :
                    log.acao === 'CREATE' ? 'text-blue-400' :
                    log.acao === 'UPDATE' ? 'text-yellow-400' :
                    log.acao === 'DELETE' ? 'text-red-400' :
                    log.acao === 'VIEW' ? 'text-purple-400' :
                    'text-gray-400'
                  }`}>
                    [{log.acao}]
                  </span>
                  <span className="ml-2 text-cyan-400">
                    [{log.modulo}]
                  </span>
                  <span className="ml-2 text-gray-300">
                    {log.usuario_nome} ({log.usuario_login})
                  </span>
                  <span className="ml-2 text-gray-400">
                    - {log.descricao}
                  </span>
                  {log.filial_atual && (
                    <span className="ml-2 text-gray-500 text-xs">
                      [{log.filial_atual}]
                    </span>
                  )}
                </div>
              ))}
              {logsUsuarios.length === 0 && (
                <div className="text-gray-500 text-center py-8">
                  Nenhum log de usuário disponível
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Conteúdo da Aba de Testes */}
      {abaAtiva === 'testes' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <AutoTestRunner />
        </div>
      )}
    </div>
  );
};

export default MonitoramentoSistema;
